<template>
  <div class="products-show-view">
    <loader v-if="isLoading" />
    <div v-if="isSuccessfullyLoaded">
      <div class="action-buttons-container">
        <div class="action-buttons">

          <link-button :to="{ name: 'edit-product', params: { id: product.id } }" icon="pencil">
            {{ $t('actions.edit') }}
          </link-button>

          <link-button :to="{ name: 'product-revisions', params: { id: product.id } }" icon="future">
            {{ $t('actions.editRevisions') }}
          </link-button>

          <common-button @click="deleteProductWhenConfirmed" type="danger" icon="trash">
            {{ $t('actions.delete') }}
          </common-button>
        </div>

        <div class="active-revision-indicator" v-if="hasActiveRevision">
          <icon name="check" />
          <b> {{ $t('hints.activeRevision') }} </b>
        </div>
      </div>

      <mini-gallery :record="product" />

      <h3>{{ $t('headlines.attributes') }}</h3>

      <attribute-table :data="product" :fields="tableFields">
        <template slot="type" scope="type">
          <span v-if="product.bundle">{{ $t('types.bundle') }}</span>
          <span v-else>{{ $t('types.standard') }}</span>
        </template>
        <template slot="family">
          <router-link :to="{ name: 'product-family', params: { id: product.family.id } }">
            {{ product.family.name }}
          </router-link>
        </template>
        <template slot="brand" v-if="product.family.brand">
          <router-link :to="{ name: 'edit-brand', params: { id: product.family.brand.id } }">
            {{ product.family.brand.name }}
          </router-link>
        </template>
        <template slot="model" scope="model">
          <router-link :to="{ name: 'product-family', params: { id: product.family.id } }">
            {{ product.family.model }}
          </router-link>
        </template>
        <template slot="totalStock">
          <router-link :to="{ name: 'product-availabilities', id: product.id }" :class="totalStockClass">
            {{ $t('unitsInStock', { count: totalStock }) }}
          </router-link>
        </template>
        <template slot="insertedAt" scope="insertedAt">
          <timeago :since="insertedAt.value" :auto-update="60" />
        </template>
        <template slot="updatedAt" scope="updatedAt">
          <timeago :since="updatedAt.value" :auto-update="60" />
        </template>
        <template slot="customizability">
          {{ $t(`productCustomizabilities.${product.customizability}`) }}
        </template>
        <template slot="manufacturerProductCode" scope="manufacturerProductCode">
          <timeago :since="manufacturerProductCode.value" :auto-update="60" />
        </template>
      </attribute-table>

      <template v-if="product.bundle">
        <h3>{{ $t('headlines.bundledProducts') }}</h3>

        <product-table v-if="bundledProducts.length" :products="bundledProducts" />

        <div v-else class="uk-placeholder uk-text-center">
          <icon name="warning" class="uk-margin-small-right" />
          {{ $t('hints.noBundledProducts') }}
        </div>
      </template>

      <h3>{{ $t('headlines.offers', { count: product.offers.length }) }}</h3>

      <link-button :to="{ name: 'new-offer', query: { productId: id } }" type="primary" icon="plus">
        {{ $t('actions.add') }}
      </link-button>

      <product-offer-table v-if="product.offers.length" :offers="product.offers" />

      <div v-else class="uk-placeholder uk-text-center">
        <icon name="warning" class="uk-margin-small-right" />
        {{ $t('hints.noOffers') }}
      </div>
    </div>
  </div>
</template>

<script>
import UIkit from 'uikit';
import AttributeTable from '@/components/AttributeTable';
import deleteProductMutation from '@/api/mutations/delete-product.graphql';
import CommonButton from '@/components/CommonButton';
import LinkButton from '@/components/LinkButton';
import Loader from '@/components/Loader';
import MiniGallery from '@/components/MiniGallery';
import Modal from '@/components/Modal';
import ProductOfferTable from '@/components/ProductOfferTable';
import ProductTable from '@/components/ProductTable';
import TextPlaceholder from '@/components/TextPlaceholder';
import Toast from '@/helpers/Toast';
import View from '@/mixins/View';
import Icon from '@/components/Icon';

export default {
  name: 'product-show-view',
  mixins: [View],
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  components: {
    AttributeTable,
    CommonButton,
    LinkButton,
    Loader,
    MiniGallery,
    Modal,
    ProductOfferTable,
    ProductTable,
    TextPlaceholder,
    Icon,
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    isSuccessfullyLoaded() {
      return this.isLoaded && this.product;
    },
    tableFields() {
      let fields = ['type'];
      if (this.product.bundle) {
        fields = fields.concat(['family', 'name', 'articleNumber']);
      } else {
        fields = fields.concat([
          'brand',
          'manufacturerProductCode',
          'model',
          'variantName',
          'articleNumber',
          'ean',
          'totalStock',
          'customizability',
        ]);
      }
      return fields.concat(['insertedAt', 'updatedAt']);
    },
    bundledProducts() {
      if (!this.product.bundle) return [];
      const bundlings = this.product.bundlings || [];
      return bundlings.map(bundling => bundling.product);
    },
    totalStock() {
      if (!this.product || this.product.bundle) return undefined;
      return this.product.availabilities
        .map(a => a.stock)
        .reduce((a, b) => a + b, 0);
    },
    totalStockClass() {
      if (this.totalStock > 0) return null;
      return 'product-total-stock--no-stock';
    },
    hasActiveRevision() {
      return this.product.activeRevision ?
        this.product.activeRevision.active : false;
    },
  },
  methods: {
    deleteProductWhenConfirmed() {
      const message = this.$t('confirmations.delete',
        { name: this.product.name });
      UIkit.modal
        .confirm(message)
        .then(() => { this.deleteProduct(); })
        .catch(() => { });
    },
    async deleteProduct() {
      await this.$apollo.mutate({
        mutation: deleteProductMutation,
        variables: { id: this.product.id },
      });
      const message = this.$t('toasts.deleted', { name: this.product.name });
      Toast.success(message);
      this.$router.push({ name: 'products' });
    },
  },
  i18n: {
    messages: {
      de: {
        breadcrumb: 'Details',
        confirmations: {
          delete: `Produkt <strong>{name}</strong> wirklich löschen?
                   Alle zugehörigen Bilder und Angebote werden ebenfalls
                   gelöscht.`,
        },
        headlines: {
          attributes: 'Eigenschaften',
          bundledProducts: 'Enthaltene Produkte',
          offers: 'Angebote ({count})',
        },
        hints: {
          noBundledProducts: `Dieses Bundle hat aktuell keine zugeordneten
                              Produkte.`,
          noOffers: 'Dieses Produkt hat aktuell keine Angebote.',
          activeRevision: 'Hier ist eine aktive Überarbeitung.',
        },
        validity: {
          unlimited: 'unbegrenzt',
        },
        toasts: {
          deleted: 'Produkt gelöscht: {name}',
        },
        types: {
          standard: 'Standard',
          bundle: 'Bundle',
        },
        unitsInStock: '{count} Einheiten',
      },
    },
  },
};
</script>

<style lang="scss">
.product-total-stock--no-stock {
  background-color: #fcc;
}


.action-buttons-container {
  display: flex;
  align-items: center;
  /* Align items vertically */
}

.action-buttons {
  display: flex;
  gap: 10px;
  /* Adjust spacing between buttons */
}

.active-revision-indicator {
  margin-left: auto;
  /* Push the "ACTIVE" indicator to the right */
  padding-right: 10px;
}
</style>



// WEBPACK FOOTER //
// ShowView.vue?2ad70246